import { useCallback, useState } from "react";

const STORAGE_PREFIX = "dismissedNote:";

const buildKey = (storageKey: string) => `${STORAGE_PREFIX}${storageKey}`;

const readIsVisible = (storageKey: string) => {
  try {
    return localStorage.getItem(buildKey(storageKey)) !== "true";
  } catch {
    // Storage is blocked (private mode) - keep showing the note.
    return true;
  }
};

/**
 * Remembers per browser whether the user closed an informational note, so a note
 * that was dismissed once never comes back. Storage can be blocked (private mode
 * on mobile Safari/Firefox), in which case the note simply keeps showing.
 */
export const useDismissibleNote = (storageKey: string) => {
  const [state, setState] = useState(() => ({
    storageKey,
    isVisible: readIsVisible(storageKey),
  }));

  // A mounted note can be pointed at another key (a per-user or per-tab note),
  // which has its own dismissed state - re-read it instead of keeping the old one.
  if (state.storageKey !== storageKey) {
    setState({ storageKey, isVisible: readIsVisible(storageKey) });
  }

  const dismiss = useCallback(() => {
    setState({ storageKey, isVisible: false });

    try {
      localStorage.setItem(buildKey(storageKey), "true");
    } catch {
      // Nothing to do - the note stays hidden for this session only.
    }
  }, [storageKey]);

  return { isVisible: state.isVisible, dismiss };
};
